import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Table, Tag, Button, Empty, Input, Row, Col, Space, Tabs } from 'antd';
import { ReloadOutlined, FilePdfOutlined, FileTextOutlined, FileDoneOutlined } from '@ant-design/icons';
import { formatDate, formatMontant } from '../../variables/constants';
import { listerFactures, ouvrirFacturePdf, TYPES_FACTURE } from '../../services/attestationsService';
import TableExportButton from '../../components/TableExportButton';

import { useTranslation } from 'react-i18next';

const { Search } = Input;

export default function FacturesTab({ refreshKey, actif }) {
  const { t } = useTranslation('attestations');
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  const [search, setSearch] = useState('');
  const [activeSubTab, setActiveSubTab] = useState('DEFINITIVE');

  const load = useCallback(async (page = 0) => {
    setLoading(true);
    try {
      const res = await listerFactures(page, 20);
      setData(res.items);
      setPagination(prev => ({ ...prev, current: res.currentPage + 1, pageSize: res.pageSize, total: res.totalElements }));
    } catch {
      setData([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(0); }, [load, refreshKey]);

  const { proformas, definitives } = useMemo(() => {
    const proformas = [];
    const definitives = [];
    data.forEach(f => {
      if (f.typeFacture === 'PROFORMA') proformas.push(f);
      else if (f.typeFacture === 'DEFINITIVE') definitives.push(f);
    });
    return { proformas, definitives };
  }, [data]);

  const filterBySearch = (list) => list.filter(f =>
    !search ||
    f.numeroFacture?.toLowerCase().includes(search.toLowerCase()) ||
    f.commandeNumero?.toLowerCase().includes(search.toLowerCase())
  );

  const filteredProformas = useMemo(() => filterBySearch(proformas), [proformas, search]);
  const filteredDefinitives = useMemo(() => filterBySearch(definitives), [definitives, search]);

  const baseColumns = [
    { title: t('commun.colonnes.numeroFacture'), dataIndex: 'numeroFacture', key: 'num', width: 150, fixed: 'left',
      render: (v) => <span style={{ fontFamily: 'var(--font-main)', fontWeight: 700, color: '#1a6b3c' }}>{v}</span> },
    { title: t('commun.colonnes.commande'), dataIndex: 'commandeNumero', key: 'cmd', width: 150,
      render: (v) => v ? <span style={{ fontFamily: 'var(--font-main)', fontSize: 12 }}>{v}</span> : '-' },
    { title: t('factures.colonnes.dateFacture'), dataIndex: 'dateFacture', key: 'df', width: 110, format: 'date', render: formatDate },
    // « Échéance » et « Sous-total attestations » retirées à la demande du métier :
    // l'échéance n'est pas suivie ici, et le sous-total se lit dans le détail de
    // la commande. Le tableau garde la contribution au fonds et le total.
    { title: t('factures.colonnes.contributionFonds'), dataIndex: 'montantContributionFonds', key: 'cf', width: 130, align: 'right', format: 'currency', numeric: true,
      render: formatMontant },
    { title: t('commun.colonnes.total'), dataIndex: 'montantTotal', key: 'tot', width: 140, align: 'right', format: 'currency', numeric: true,
      render: (v) => <strong style={{ color: '#1a6b3c' }}>{formatMontant(v)}</strong> },
    { title: t('factures.colonnes.beneficiaire'), dataIndex: 'instructionCheque', key: 'ben', ellipsis: true,
      render: (v) => v || '-' },
    { title: t('commun.colonnes.etat'), dataIndex: 'annulee', key: 'an', width: 100,
      export: (v) => v ? t('commun.annulee') : t('commun.active'),
      render: (v) => v ? <Tag color="red">{t('commun.annulee')}</Tag> : <Tag color="green">{t('commun.active')}</Tag> },
    { title: '', key: 'pdf', width: 90, fixed: 'right', exportable: false,
      render: (_, f) => (
        <Button size="small" icon={<FilePdfOutlined />}
          onClick={() => ouvrirFacturePdf(f.factureTrackingId)}
          style={{ color: '#b91c1c', borderColor: '#fecaca' }}>
          PDF
        </Button>
      ) },
  ];

  const renderTable = (rows, typeFacture) => (
    <>
      <Row gutter={[12, 12]} align="middle" style={{ marginBottom: 16 }}>
        <Col xs={24} md={14}>
          <Search placeholder={t('factures.recherche')} value={search}
            onChange={(e) => setSearch(e.target.value)} allowClear />
        </Col>
        <Col xs={24} md={10} style={{ textAlign: 'right' }}>
          <Space>
            <Button icon={<ReloadOutlined />} onClick={() => load(pagination.current - 1)}>{t('commun.actualiser')}</Button>
          </Space>
        </Col>
      </Row>

      <Table className="cb-table" dataSource={rows} columns={baseColumns} rowKey="factureTrackingId"
        loading={loading} scroll={{ x: 1130 }}
        pagination={{
          pageSize: pagination.pageSize,
          showTotal: (total) => t(`factures.total.${typeFacture}`, { total }),
        }}
        locale={{ emptyText: <Empty description={t(`factures.vide.${typeFacture}`)} image={Empty.PRESENTED_IMAGE_SIMPLE} /> }} />
    </>
  );

  /* L'EXPORT SUIT LE SOUS-ONGLET, ET N'EST RENDU QU'UNE FOIS. Il vivait dans
     `renderTable`, appelee pour chacun des deux sous-onglets : rendu flottant, il
     s'y serait trouve deux fois, et deux fois de plus si les autres onglets de
     l'ecran avaient ete visites. Il sort donc des sous-onglets, et ce sont ses
     donnees qui suivent le sous-onglet actif. */
  const proforma = activeSubTab === 'PROFORMA';
  const lignesExport = proforma ? filteredProformas : filteredDefinitives;

  return (
    <>
    <Tabs
      activeKey={activeSubTab}
      onChange={setActiveSubTab}
      items={[
        {
          key: 'DEFINITIVE',
          label: (
            <span>
              <FileDoneOutlined /> {t('factures.onglets.definitives')}
              <Tag color="green" style={{ marginLeft: 8, borderRadius: 20 }}>{definitives.length}</Tag>
            </span>
          ),
          children: renderTable(filteredDefinitives, 'facture'),
        },
        {
          key: 'PROFORMA',
          label: (
            <span>
              <FileTextOutlined /> {t('factures.onglets.proformas')}
              <Tag color="blue" style={{ marginLeft: 8, borderRadius: 20 }}>{proformas.length}</Tag>
            </span>
          ),
          children: renderTable(filteredProformas, 'proforma'),
        },
      ]}
    />

      {actif && (
        <TableExportButton
          data={lignesExport}
          columns={baseColumns}
          filename={proforma ? 'factures-proformas' : 'factures-definitives'}
          title={proforma ? t('factures.onglets.proformas') : t('factures.onglets.definitives')}
          subtitle={search ? t('commun.recherche', { search }) : undefined}
          floating
        />
      )}
    </>
  );
}
